import React, { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';
import { Calendar, ChevronDown, ChevronRight, MapPin, Phone, DollarSign, RefreshCw, User, Printer } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import apiService from '../services/apiService';

export default function AgendaCobrancas() {
  const { getAgendamentosDia, getClientesEmAtraso } = useApi();
  
  const [loading, setLoading] = useState(true);
  const [agendamentosSemana, setAgendamentosSemana] = useState({});
  const [diasExpandidos, setDiasExpandidos] = useState({});
  const [semanaAtual, setSemanaAtual] = useState(0); // 0 = semana atual, 1 = próxima, -1 = anterior
  const [imprimindo, setImprimindo] = useState(false);

  useEffect(() => {
    loadAgendamentos();
  }, [semanaAtual]);

  const loadAgendamentos = async () => {
    try {
      setLoading(true);
      
      // Buscar todos os clientes em atraso para obter os dados completos
      const clientesData = await getClientesEmAtraso(30);
      const todosClientes = clientesData.clientes || [];
      
      // Calcular dias da semana
      const diasDaSemana = getDiasDaSemana(semanaAtual);
      
      // Buscar agendamentos para cada dia
      const agendamentos = {};
      
      for (const dia of diasDaSemana) {
        const dataFormatada = formatDateToISO(dia.data);
        
        // Filtrar clientes que tÃªm cobranÃ§a presencial marcada e data de agendamento correspondente
        const clientesAgendados = todosClientes.filter(cliente => {
          if (!cliente.cobranca_presencial) return false;
          if (!cliente.data_agendamento) return false;
          
          // Comparar apenas a data (sem hora)
          const dataAgendamento = new Date(cliente.data_agendamento);
          const dataAgendamentoStr = dataAgendamento.toISOString().split('T')[0];
          
          return dataAgendamentoStr === dataFormatada;
        });
        
        agendamentos[dia.diaSemana] = {
          data: dia.data,
          dataFormatada: formatDateBR(dia.data),
          clientes: clientesAgendados
        };
      }
      
      setAgendamentosSemana(agendamentos);
      
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
      alert('Erro ao carregar agenda de cobranÃ§as');
    } finally {
      setLoading(false);
    }
  };

  const getDiasDaSemana = (offsetSemanas = 0) => {
    const hoje = new Date();
    const diaSemana = hoje.getDay(); // 0 = domingo, 1 = segunda, etc.
    
    // Calcular segunda-feira da semana
    const segunda = new Date(hoje);
    const diasParaSegunda = diaSemana === 0 ? -6 : 1 - diaSemana;
    segunda.setDate(hoje.getDate() + diasParaSegunda + (offsetSemanas * 7));
    
    const dias = [];
    const nomesDias = ['Segunda', 'TerÃ§a', 'Quarta', 'Quinta', 'Sexta', 'SÃ¡bado', 'Domingo'];
    
    for (let i = 0; i < 7; i++) {
      const dia = new Date(segunda);
      dia.setDate(segunda.getDate() + i);
      
      dias.push({
        diaSemana: nomesDias[i],
        data: dia,
        ehHoje: isToday(dia)
      });
    }
    
    return dias;
  };

  const isToday = (data) => {
    const hoje = new Date();
    return data.toDateString() === hoje.toDateString();
  };

  const formatDateToISO = (data) => {
    return data.toISOString().split('T')[0];
  };

  const formatDateBR = (data) => {
    return data.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatarTelefone = (tel) => {
    if (!tel) return '';
    const numero = tel.replace(/\D/g, '');
    if (numero.length === 11) {
      return `(${numero.substr(0,2)}) ${numero.substr(2,5)}-${numero.substr(7)}`;
    }
    if (numero.length === 10) {
      return `(${numero.substr(0,2)}) ${numero.substr(2,4)}-${numero.substr(6)}`;
    }
    return tel;
  };

  /**
   * Buscar contas a receber de um cliente
   */
  const buscarContasCliente = async (ordemCliente) => {
    try {
      const response = await apiService.clientes.buscarContas(ordemCliente);
      
      let contasEncontradas = [];
      if (response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          contasEncontradas = response.data.data;
        } else if (response.data.contas && Array.isArray(response.data.contas)) {
          contasEncontradas = response.data.contas;
        } else if (Array.isArray(response.data)) {
          contasEncontradas = response.data;
        }
      }
      
      return contasEncontradas;
    } catch (error) {
      console.error('Erro ao buscar contas do cliente:', error);
      return [];
    }
  };

  /**
   * Imprimir agenda do dia
   */
  const imprimirAgendaDia = async (diaSemana, dados) => {
    if (dados.clientes.length === 0) {
      alert('Não há clientes agendados para este dia');
      return;
    }

    setImprimindo(true);

    try {
      // Buscar contas de todos os clientes
      const clientesComContas = await Promise.all(
        dados.clientes.map(async (cliente) => {
          const contas = await buscarContasCliente(cliente.ordem_cliente);
          return {
            ...cliente,
            contas: contas.filter(conta => conta.Dias_Atraso > 0) // Apenas contas em atraso
          };
        })
      );

      // Criar HTML para impressão
      const htmlImpressao = gerarHTMLImpressao(diaSemana, dados.dataFormatada, clientesComContas);

      // Criar janela de impressão
      const janelaImpressao = window.open('', '_blank');
      janelaImpressao.document.write(htmlImpressao);
      janelaImpressao.document.close();

      // Aguardar carregamento e imprimir
      janelaImpressao.onload = () => {
        setTimeout(() => {
          janelaImpressao.print();
          setImprimindo(false);
        }, 250);
      };
    } catch (error) {
      console.error('Erro ao preparar impressão:', error);
      alert('Erro ao preparar impressão da agenda');
      setImprimindo(false);
    }
  };

  /**
   * Gerar HTML formatado para impressão
   */
  const gerarHTMLImpressao = (diaSemana, dataFormatada, clientes) => {
    const clientesHTML = clientes
      .map(
        (cliente, index) => `
        <div class="cliente-bloco">
          <div class="cliente-numero">${index + 1}</div>
          <div class="cliente-info">
            <h3>${cliente.nome_cliente || 'Nome não disponível'}</h3>
            <p><strong>Código:</strong> ${cliente.codigo_cliente || 'N/A'}</p>
            
            <div class="info-row">
              <div class="info-item">
                <strong>📍 Endereço:</strong>
                <p>
                  ${cliente.endereco || 'Endereço não disponível'}
                  ${cliente.numero ? `, ${cliente.numero}` : ''}
                  ${cliente.bairro ? `<br>${cliente.bairro}` : ''}
                  ${cliente.cidade || cliente.estado ? `<br>${cliente.cidade || ''}${cliente.cidade && cliente.estado ? ' - ' : ''}${cliente.estado || ''}` : ''}
                </p>
              </div>
            </div>

            <div class="info-row">
              <div class="info-item">
                <strong>📞 Telefone:</strong>
                <p>${cliente.telefone ? formatarTelefone(cliente.telefone) : 'Não disponível'}</p>
              </div>
            </div>

            <div class="info-row">
              <div class="info-item">
                <strong>📅 Contas em Atraso:</strong>
                ${
                  cliente.contas && cliente.contas.length > 0
                    ? `
                    <ul class="contas-lista">
                      ${cliente.contas
                        .map(
                          (conta) => `
                        <li>
                          <span>Vencimento: ${format(new Date(conta.Data_Vencimento), 'dd/MM/yyyy', { locale: ptBR })}</span>
                          <span class="atraso">(${conta.Dias_Atraso} dias de atraso)</span>
                          <span class="nota">Nota: ${conta.Nota || 'N/A'}</span>
                        </li>
                      `
                        )
                        .join('')}
                    </ul>
                    <p class="valor-consulta"><strong>⚠️ Valor a receber: CONSULTAR NA LOJA</strong></p>
                  `
                    : '<p>Nenhuma conta em atraso encontrada</p>'
                }
              </div>
            </div>

            ${
              cliente.resposta
                ? `
              <div class="info-row">
                <div class="info-item">
                  <strong>💬 Última Resposta:</strong>
                  <p>${
                    {
                      pagara_semana: 'Pagará essa semana',
                      pagara_mes: 'Pagará esse mês',
                      sem_previsao: 'Sem previsão de pagamento',
                      sem_contato: 'Não conseguido contato'
                    }[cliente.resposta] || cliente.resposta
                  }</p>
                </div>
              </div>
            `
                : ''
            }

            <div class="assinatura-linha">
              <div class="assinatura-box">
                <p>Assinatura: _________________________________</p>
                <p class="assinatura-label">Cobrador</p>
              </div>
              <div class="assinatura-box">
                <p>Assinatura: _________________________________</p>
                <p class="assinatura-label">Cliente</p>
              </div>
            </div>

            <div class="observacoes">
              <strong>📝 Observações:</strong>
              <p class="obs-linha">_________________________________________________________________</p>
              <p class="obs-linha">_________________________________________________________________</p>
            </div>
          </div>
        </div>
      `
      )
      .join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Agenda de Cobranças - ${diaSemana} (${dataFormatada})</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            background: white;
          }

          .cabecalho {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #333;
            padding-bottom: 15px;
          }

          .cabecalho h1 {
            font-size: 24px;
            margin-bottom: 5px;
            color: #333;
          }

          .cabecalho h2 {
            font-size: 18px;
            color: #666;
            margin-bottom: 10px;
          }

          .cabecalho p {
            font-size: 14px;
            color: #666;
          }

          .resumo {
            background: #f5f5f5;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 25px;
            border: 1px solid #ddd;
          }

          .resumo p {
            font-size: 14px;
            margin-bottom: 5px;
          }

          .cliente-bloco {
            page-break-inside: avoid;
            border: 2px solid #333;
            padding: 20px;
            margin-bottom: 25px;
            border-radius: 8px;
            background: white;
            position: relative;
          }

          .cliente-numero {
            position: absolute;
            top: 15px;
            right: 15px;
            background: #333;
            color: white;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            font-weight: bold;
          }

          .cliente-info h3 {
            font-size: 20px;
            margin-bottom: 10px;
            color: #333;
            padding-right: 50px;
          }

          .cliente-info > p {
            font-size: 13px;
            color: #666;
            margin-bottom: 15px;
          }

          .info-row {
            margin-bottom: 15px;
          }

          .info-item {
            background: #f9f9f9;
            padding: 12px;
            border-radius: 5px;
            border-left: 4px solid #4F46E5;
          }

          .info-item strong {
            display: block;
            font-size: 13px;
            color: #333;
            margin-bottom: 8px;
          }

          .info-item p {
            font-size: 13px;
            color: #666;
            line-height: 1.6;
          }

          .contas-lista {
            list-style: none;
            margin-top: 8px;
          }

          .contas-lista li {
            background: white;
            padding: 8px 12px;
            margin-bottom: 5px;
            border-radius: 4px;
            border: 1px solid #ddd;
            font-size: 12px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
          }

          .contas-lista li span {
            margin-right: 10px;
          }

          .atraso {
            color: #DC2626;
            font-weight: bold;
          }

          .nota {
            color: #666;
            font-style: italic;
          }

          .valor-consulta {
            margin-top: 12px;
            padding: 10px;
            background: #FEF3C7;
            border: 2px solid #F59E0B;
            border-radius: 5px;
            text-align: center;
            font-size: 14px;
            color: #92400E;
          }

          .assinatura-linha {
            margin-top: 25px;
            display: flex;
            justify-content: space-between;
            gap: 20px;
            padding-top: 20px;
            border-top: 2px dashed #ddd;
          }

          .assinatura-box {
            flex: 1;
          }

          .assinatura-box p {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
          }

          .assinatura-label {
            font-size: 11px;
            color: #999;
            text-align: center;
            margin-top: 5px;
          }

          .observacoes {
            margin-top: 20px;
            padding-top: 15px;
            border-top: 1px solid #ddd;
          }

          .observacoes strong {
            display: block;
            font-size: 13px;
            color: #333;
            margin-bottom: 10px;
          }

          .obs-linha {
            font-size: 12px;
            color: #999;
            margin-bottom: 8px;
          }

          .rodape {
            text-align: center;
            margin-top: 30px;
            padding-top: 15px;
            border-top: 2px solid #333;
            font-size: 11px;
            color: #999;
          }

          @media print {
            body {
              padding: 10px;
            }

            .cliente-bloco {
              page-break-inside: avoid;
              margin-bottom: 20px;
            }
          }
        </style>
      </head>
      <body>
        <div class="cabecalho">
          <h1>📋 AGENDA DE COBRANÇAS PRESENCIAIS</h1>
          <h2>${diaSemana} - ${dataFormatada}</h2>
          <p>Gerado em: ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
        </div>

        <div class="resumo">
          <p><strong>Total de Clientes:</strong> ${clientes.length}</p>
          <p><strong>Instruções:</strong> Consultar valor exato na loja. Obter assinatura do cliente após cobrança.</p>
        </div>

        ${clientesHTML}

        <div class="rodape">
          <p>Sistema de Gestão de Cobranças | Documento gerado automaticamente</p>
        </div>
      </body>
      </html>
    `;
  };

  const toggleDia = (diaSemana) => {
    setDiasExpandidos(prev => ({
      ...prev,
      [diaSemana]: !prev[diaSemana]
    }));
  };

  const getTotalValorDia = (clientes) => {
    return clientes.reduce((sum, c) => sum + (c.valor_em_aberto || 0), 0);
  };

  const getResumoDia = (clientes) => {
    if (clientes.length === 0) return 'Nenhuma cobranÃ§a agendada';
    if (clientes.length === 1) return '1 cliente agendado';
    return `${clientes.length} clientes agendados`;
  };

  const navigateWeek = (direction) => {
    setSemanaAtual(prev => prev + direction);
  };

  const getPeriodoSemana = () => {
    const dias = getDiasDaSemana(semanaAtual);
    const primeira = dias[0].data;
    const ultima = dias[6].data;
    
    return `${formatDateBR(primeira)} - ${formatDateBR(ultima)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Carregando agenda..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agenda de CobranÃ§as Presenciais</h1>
          <p className="text-gray-600 mt-1">Organize suas visitas de cobranÃ§a da semana</p>
        </div>
        <button
          onClick={loadAgendamentos}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* NavegaÃ§Ã£o de Semanas */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateWeek(-1)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            â† Semana Anterior
          </button>
          
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-600" />
            <span className="text-lg font-semibold text-gray-900">
              {getPeriodoSemana()}
            </span>
            {semanaAtual === 0 && (
              <StatusBadge status="info" text="Semana Atual" />
            )}
          </div>
          
          <button
            onClick={() => navigateWeek(1)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            PrÃ³xima Semana â†’
          </button>
        </div>
      </div>

      {/* Resumo da Semana */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Total de CobranÃ§as</p>
          <p className="text-2xl font-bold text-gray-900">
            {Object.values(agendamentosSemana).reduce((sum, dia) => sum + dia.clientes.length, 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Valor Total a Cobrar</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(
              Object.values(agendamentosSemana).reduce(
                (sum, dia) => sum + getTotalValorDia(dia.clientes), 
                0
              )
            )}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-600">Dias com Agendamentos</p>
          <p className="text-2xl font-bold text-gray-900">
            {Object.values(agendamentosSemana).filter(dia => dia.clientes.length > 0).length}
          </p>
        </div>
      </div>

      {/* Lista de Dias da Semana */}
      <div className="space-y-3">
        {Object.entries(agendamentosSemana).map(([diaSemana, dados]) => {
          const ehHoje = isToday(dados.data);
          const expandido = diasExpandidos[diaSemana];
          const temClientes = dados.clientes.length > 0;

          return (
            <div 
              key={diaSemana} 
              className={`bg-white rounded-lg shadow overflow-hidden ${
                ehHoje ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              {/* Cabeçalho do Dia - Sempre Visível */}
              <div className="flex items-center justify-between p-4">
                <div
                  onClick={() => toggleDia(diaSemana)}
                  className="flex items-center gap-4 flex-1 cursor-pointer hover:bg-gray-50 transition-colors py-2 px-2 rounded-lg"
                >
                  {/* Ícone de Expansão */}
                  <button className="text-gray-400 hover:text-gray-600">
                    {expandido ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </button>

                  {/* Informações do Dia */}
                  <div className="flex items-center gap-3">
                    <div className={`px-3 py-1 rounded-lg ${
                      ehHoje 
                        ? 'bg-blue-100 text-blue-800 font-semibold' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {diaSemana}
                    </div>
                    <span className="text-gray-600">{dados.dataFormatada}</span>
                    {ehHoje && (
                      <StatusBadge status="info" text="Hoje" />
                    )}
                  </div>
                </div>

                {/* Resumo do Dia e Botão Imprimir */}
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm text-gray-600">{getResumoDia(dados.clientes)}</p>
                    {temClientes && (
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(getTotalValorDia(dados.clientes))}
                      </p>
                    )}
                  </div>

                  {temClientes && (
                    <>
                      <StatusBadge 
                        status={dados.clientes.length > 5 ? 'warning' : 'success'} 
                        text={dados.clientes.length.toString()} 
                      />
                      
                      {/* Botão Imprimir */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          imprimirAgendaDia(diaSemana, dados);
                        }}
                        disabled={imprimindo}
                        className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Imprimir agenda do dia"
                      >
                        <Printer className="w-4 h-4" />
                        <span className="text-sm font-medium">Imprimir</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Lista de Clientes Expandida */}
              {expandido && (
                <div className="border-t border-gray-200 bg-gray-50">
                  {dados.clientes.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>Nenhuma cobranÃ§a agendada para este dia</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {dados.clientes.map((cliente, index) => (
                        <div 
                          key={cliente.ordem_cliente || index} 
                          className="p-4 hover:bg-white transition-colors"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Coluna 1: InformaÃ§Ãµes do Cliente */}
                            <div className="space-y-2">
                              <div className="flex items-start gap-2">
                                <User className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 truncate">
                                    {cliente.nome_cliente}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    CÃ³digo: {cliente.codigo_cliente || 'N/A'}
                                  </p>
                                </div>
                              </div>

                              {cliente.telefone && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Phone className="w-4 h-4" />
                                  <span>{formatarTelefone(cliente.telefone)}</span>
                                </div>
                              )}
                            </div>

                            {/* Coluna 2: EndereÃ§o */}
                            <div className="space-y-2">
                              <div className="flex items-start gap-2">
                                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0 text-sm text-gray-600">
                                  {cliente.endereco && (
                                    <>
                                      <p className="truncate">
                                        {cliente.endereco}
                                        {cliente.numero && `, ${cliente.numero}`}
                                      </p>
                                      {cliente.bairro && <p>{cliente.bairro}</p>}
                                      {(cliente.cidade || cliente.estado) && (
                                        <p>
                                          {cliente.cidade}
                                          {cliente.cidade && cliente.estado && ' - '}
                                          {cliente.estado}
                                        </p>
                                      )}
                                    </>
                                  )}
                                  {!cliente.endereco && (
                                    <p className="text-gray-400 italic">EndereÃ§o nÃ£o disponÃ­vel</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Coluna 3: Valores e Status */}
                            <div className="space-y-3">
                              <div className="flex items-start gap-2">
                                <DollarSign className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-sm text-gray-600">Valor em Aberto</p>
                                  <p className="text-lg font-bold text-gray-900">
                                    {formatCurrency(cliente.valor_em_aberto)}
                                  </p>
                                  <StatusBadge
                                    status={cliente.dias_atraso >= 90 ? 'danger' : 'warning'}
                                    text={`${cliente.dias_atraso} dias de atraso`}
                                  />
                                </div>
                              </div>

                              {/* InformaÃ§Ãµes Adicionais */}
                              {cliente.resposta && (
                                <div className="text-xs">
                                  <span className="text-gray-600">Resposta: </span>
                                  <span className="text-gray-900">
                                    {cliente.resposta === 'pagara_semana' && 'PagarÃ¡ essa semana'}
                                    {cliente.resposta === 'pagara_mes' && 'PagarÃ¡ esse mÃªs'}
                                    {cliente.resposta === 'sem_previsao' && 'Sem previsÃ£o'}
                                    {cliente.resposta === 'sem_contato' && 'Sem contato'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legenda */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>ðŸ’¡ Dica:</strong> Clique em um dia da semana para expandir e ver todos os clientes agendados. 
          Os dias destacados em azul sÃ£o o dia atual.
        </p>
      </div>
    </div>
  );
}